import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Mic,
  MicOff,
  Play,
  Pause,
  Square,
  Save,
  Trash2,
  FileUp,
  Loader2,
  Globe,
  Languages,
  Gauge,
  User,
  AlertCircle,
  FolderOpen,
  HardDrive,
  Cloud,
  X,
  Pencil,
  Eye,
  Copy,
  Check,
  Share2,
  Ban,
  Link2,
  Paperclip,
  FileText,
  Loader2 as LoaderIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useIdeas, useCategories } from "@/hooks/useIdeas";
import { SendToUserDialog } from "@/components/SendToUserDialog";
import { AppHeader } from "@/components/AppHeader";
import { IdeasPanel } from "@/components/IdeasPanel";
import { WelcomeTour } from "@/components/WelcomeTour";
import { InteractiveGuide } from "@/components/InteractiveGuide";
import { PromoBanner } from "@/components/PromoBanner";

// Status types
const STATUS = {
  IDLE: "idle",
  RECORDING: "recording",
  PLAYING: "playing",
  PAUSED: "paused",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const VoiceIdeias = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDark, toggleTheme } = useTheme();
  
  // Auth
  const { user, isAuthenticated, hasAccess, getStatusInfo, logout, token } = useAuth();
  
  // Ideas hook
  const { ideas, loading: ideasLoading, createIdea, updateIdea, deleteIdea, toggleFavorite, isLocal } = useIdeas();
  const CATEGORIES = useCategories();
  
  // State
  const [text, setText] = useState("");
  const [status, setStatus] = useState(STATUS.IDLE);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [voiceGender, setVoiceGender] = useState("female");
  const [voiceLang, setVoiceLang] = useState("pt-BR");
  const [speed, setSpeed] = useState([1]);
  const [isSupported, setIsSupported] = useState({ tts: false, stt: false });
  
  // Ideas UI state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [ideaTitle, setIdeaTitle] = useState("");
  const [ideaCategory, setIdeaCategory] = useState("Pessoal");
  const [showIdeasPanel, setShowIdeasPanel] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editingIdea, setEditingIdea] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [savingIdea, setSavingIdea] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [playingIdeaId, setPlayingIdeaId] = useState(null);
  const [sharingIdeaId, setSharingIdeaId] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const [showSharesPanel, setShowSharesPanel] = useState(false);
  const [sharedLinks, setSharedLinks] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [newViewsCount, setNewViewsCount] = useState(0);
  
  // Internal messaging state
  const [sendToUserIdeaId, setSendToUserIdeaId] = useState(null);
  const [sendToUserIdeaTitle, setSendToUserIdeaTitle] = useState("");
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Layout mode state
  const [layoutMode, setLayoutMode] = useState("panel");
  
  // Focus mode state
  const [focusMode, setFocusMode] = useState(false);

  // Tutorial state
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [showInteractiveGuide, setShowInteractiveGuide] = useState(false);

  // TTS highlight state
  const [highlightWordIndex, setHighlightWordIndex] = useState(-1);
  const highlightRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const wordsRef = useRef([]);

  // Refs
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const utteranceRef = useRef(null);
  const titleInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const keepAliveRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkIndexRef = useRef(0);
  
  // Document import state
  const [importing, setImporting] = useState(false);

  // Share config state
  const [shareAllowSave, setShareAllowSave] = useState(true);
  const [shareExpiration, setShareExpiration] = useState("none");
  const [shareStep, setShareStep] = useState("config");
  const [shareRecipient, setShareRecipient] = useState("");
  const [shareAttachments, setShareAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // ===================== EFFECTS =====================

  // Focus mode: ESC to exit
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && focusMode) setFocusMode(false);
    };
    if (focusMode) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusMode]);

  // Auto-scroll textarea during TTS playback
  useEffect(() => {
    if (highlightWordIndex >= 0 && wordsRef.current.length > 0) {
      const progress = highlightWordIndex / wordsRef.current.length;
      const textarea = document.querySelector('[data-testid="main-textarea"]');
      if (textarea && textarea.scrollHeight > textarea.clientHeight) {
        textarea.scrollTo({ top: (textarea.scrollHeight - textarea.clientHeight) * progress, behavior: "smooth" });
      }
      const focusTextarea = document.querySelector('[data-testid="focus-textarea"]');
      if (focusTextarea && focusTextarea.scrollHeight > focusTextarea.clientHeight) {
        focusTextarea.scrollTo({ top: (focusTextarea.scrollHeight - focusTextarea.clientHeight) * progress, behavior: "smooth" });
      }
    }
  }, [highlightWordIndex]);

  // Redirect to blocked page if user's access expired
  useEffect(() => {
    if (isAuthenticated && !hasAccess) {
      navigate("/bloqueado");
    }
  }, [isAuthenticated, hasAccess, navigate]);

  // Show welcome tour on first visit
  useEffect(() => {
    const seen = localStorage.getItem("audio-ideias-tour-seen");
    if (!seen) {
      const timer = setTimeout(() => setShowWelcomeTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Load text from URL params
  useEffect(() => {
    const texto = searchParams.get("texto");
    if (texto) {
      setText(decodeURIComponent(texto));
      setSearchParams({}, { replace: true });
      toast.success("Texto carregado! Salve como ideia ou ouça.");
    }
  }, []);

  // Check browser support and load voices
  useEffect(() => {
    const ttsSupported = "speechSynthesis" in window;
    const sttSupported = "SpeechRecognition" in window || "webkitSpeechRecognition" in window;
    setIsSupported({ tts: ttsSupported, stt: sttSupported });

    if (ttsSupported) {
      synthRef.current = window.speechSynthesis;
      loadVoices();
      synthRef.current.onvoiceschanged = loadVoices;
    }

    if (sttSupported) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "pt-BR";

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript) {
          setText((prev) => prev + finalTranscript + " ");
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        if (event.error === "not-allowed") {
          toast.error("Permissão de microfone negada.");
        } else if (event.error === "audio-capture") {
          toast.error("Microfone não detectado.");
        } else if (event.error === "network") {
          toast.error("Erro de rede no reconhecimento de voz.");
        } else if (event.error !== "no-speech" && event.error !== "aborted") {
          toast.error("Erro no reconhecimento de voz.");
        }
        setStatus(STATUS.IDLE);
      };

      recognitionRef.current.onend = () => {
        if (status === STATUS.RECORDING) {
          try { recognitionRef.current.start(); } catch { setStatus(STATUS.IDLE); }
        }
      };
    }

    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
      if (synthRef.current) synthRef.current.cancel();
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      stopHighlightTimer();
    };
  }, []);

  // Fetch shares & unread on mount
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchSharedLinks();
      axios.get(`${BACKEND_URL}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => setUnreadMessages(res.data.unread || 0)).catch(() => {});
      axios.get(`${BACKEND_URL}/api/user/layout`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => setLayoutMode(res.data.layout_mode || "panel")).catch(() => {});
    }
  }, [isAuthenticated, token]);

  // Select best voice when language or gender changes
  useEffect(() => {
    if (voices.length === 0) return;
    const langVoices = voices.filter(v => v.lang === voiceLang);
    if (langVoices.length === 0) return;
    const currentValid = filteredVoices.some(v => v.name === selectedVoice);
    if (currentValid) return;
    const map = VOICE_GENDER_MAP[voiceLang];
    if (map && voiceGender !== "all") {
      const preferred = map[voiceGender] || [];
      for (const prefName of preferred) {
        const found = langVoices.find(v => v.name.includes(prefName));
        if (found) { setSelectedVoice(found.name); return; }
      }
    }
    if (filteredVoices.length > 0) { setSelectedVoice(filteredVoices[0].name); return; }
    if (langVoices.length > 0) { setSelectedVoice(langVoices[0].name); }
  }, [voiceGender, voiceLang, voices.length]);

  // ===================== VOICE HELPERS =====================

  const loadVoices = useCallback(() => {
    const availableVoices = synthRef.current.getVoices();
    if (availableVoices.length === 0) return;
    setVoices(availableVoices);
  }, []);

  const VOICE_GENDER_MAP = {
    "pt-BR": {
      female: ["Google português do Brasil", "Microsoft Francisca Online (Natural) - Portuguese (Brazil)", "Microsoft Maria Online (Natural) - Portuguese (Brazil)", "Luciana", "Francisca"],
      male: ["Microsoft Antonio Online (Natural) - Portuguese (Brazil)", "Microsoft Humberto Online (Natural) - Portuguese (Brazil)", "Daniel", "Antonio"],
    },
    "pt-PT": {
      female: ["Google português", "Microsoft Raquel Online (Natural) - Portuguese (Portugal)"],
      male: ["Microsoft Duarte Online (Natural) - Portuguese (Portugal)"],
    },
    "en-US": {
      female: ["Google US English", "Microsoft Jenny Online (Natural) - English (United States)", "Samantha"],
      male: ["Microsoft Guy Online (Natural) - English (United States)", "Microsoft Eric Online (Natural) - English (United States)", "Alex"],
    },
    "es-ES": {
      female: ["Google español", "Microsoft Elvira Online (Natural) - Spanish (Spain)"],
      male: ["Microsoft Alvaro Online (Natural) - Spanish (Spain)"],
    },
  };

  const availableLanguages = [...new Set(voices.map(v => v.lang))].sort((a, b) => {
    if (a.startsWith("pt") && !b.startsWith("pt")) return -1;
    if (!a.startsWith("pt") && b.startsWith("pt")) return 1;
    return a.localeCompare(b);
  });

  const getVoiceGender = (voice) => {
    const map = VOICE_GENDER_MAP[voice.lang];
    if (map) {
      if (map.female && map.female.some(n => voice.name.includes(n))) return "female";
      if (map.male && map.male.some(n => voice.name.includes(n))) return "male";
    }
    const name = voice.name.toLowerCase();
    const femaleNames = ["female", "feminino", "maria", "francisca", "luciana", "alice", "camila", "jenny", "samantha", "elvira", "raquel"];
    const maleNames = ["male", "masculino", "antonio", "daniel", "humberto", "guy", "eric", "alex", "duarte", "alvaro"];
    if (femaleNames.some(h => name.includes(h))) return "female";
    if (maleNames.some(h => name.includes(h))) return "male";
    return "unknown";
  };

  const availableGenders = (() => {
    const langVoices = voices.filter(v => v.lang === voiceLang);
    const genders = { female: false, male: false };
    for (const v of langVoices) {
      const g = getVoiceGender(v);
      if (g === "female") genders.female = true;
      if (g === "male") genders.male = true;
    }
    return genders;
  })();

  const genderWarning = (() => {
    if (voiceGender === "all" || voices.length === 0) return null;
    if (voiceGender === "male" && !availableGenders.male) return "Este dispositivo não possui voz masculina para " + voiceLang;
    if (voiceGender === "female" && !availableGenders.female) return "Este dispositivo não possui voz feminina para " + voiceLang;
    return null;
  })();

  const filteredVoices = voices.filter((v) => {
    if (v.lang !== voiceLang) return false;
    if (voiceGender === "all") return true;
    const gender = getVoiceGender(v);
    if (voiceGender === "male" && availableGenders.male) return gender === "male";
    if (voiceGender === "female" && availableGenders.female) return gender === "female";
    return true;
  });

  // ===================== TTS HIGHLIGHT =====================

  const startHighlightTimer = (content, rate) => {
    const words = [];
    const regex = /\S+/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      words.push({ word: match[0], index: match.index });
    }
    wordsRef.current = words;
    if (words.length === 0) return;
    const msPerWord = (60000 / (150 * rate));
    let wordIdx = 0;
    setHighlightWordIndex(0);
    highlightTimerRef.current = setInterval(() => {
      wordIdx++;
      if (wordIdx >= words.length) {
        clearInterval(highlightTimerRef.current);
        highlightTimerRef.current = null;
        return;
      }
      setHighlightWordIndex(wordIdx);
    }, msPerWord);
  };

  const stopHighlightTimer = () => {
    if (highlightTimerRef.current) {
      clearInterval(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    setHighlightWordIndex(-1);
    wordsRef.current = [];
  };

  const renderHighlightedText = (content, className = "", style = {}) => {
    if (highlightWordIndex < 0 || status !== STATUS.PLAYING) return null;
    const words = wordsRef.current;
    if (!words.length) return null;
    const progress = Math.min((highlightWordIndex / words.length) * 100, 100);
    return (
      <div
        ref={highlightRef}
        className={`pointer-events-none ${className}`}
        style={{ ...style, position: "absolute", inset: 0, overflow: "hidden", zIndex: 2 }}
        data-testid="tts-highlight-overlay"
      >
        <div
          className="absolute left-0 top-0 w-[3px] rounded-full transition-all duration-500 ease-out"
          style={{ height: `${progress}%`, background: 'linear-gradient(to bottom, rgba(99,102,241,0.15), rgba(99,102,241,0.4))' }}
          data-testid="tts-progress-bar"
        />
      </div>
    );
  };

  // ===================== TTS FUNCTIONS =====================

  // Split text into chunks (sentences) to avoid Chrome's speech timeout bug
  const splitIntoChunks = (inputText) => {
    const maxLen = 200;
    const sentences = inputText.match(/[^.!?\n]+[.!?\n]*/g) || [inputText];
    const result = [];
    let current = "";
    for (const s of sentences) {
      if ((current + s).length > maxLen && current) {
        result.push(current.trim());
        current = s;
      } else {
        current += s;
      }
    }
    if (current.trim()) result.push(current.trim());
    return result.length > 0 ? result : [inputText];
  };

  const stopKeepAlive = () => {
    if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
  };

  const speakChunk = (chunkIdx) => {
    if (chunkIdx >= chunksRef.current.length) {
      setStatus(STATUS.IDLE);
      stopHighlightTimer();
      stopKeepAlive();
      return;
    }
    chunkIndexRef.current = chunkIdx;
    const chunk = chunksRef.current[chunkIdx];
    const utterance = new SpeechSynthesisUtterance(chunk);
    utterance.rate = speed[0];
    utterance.lang = voiceLang;
    const v = voices.find((vc) => vc.name === selectedVoice);
    if (v) utterance.voice = v;
    utterance.onend = () => {
      speakChunk(chunkIdx + 1);
    };
    utterance.onerror = (event) => {
      if (event.error !== "interrupted" && event.error !== "cancelled") {
        toast.error("Erro na reproducao de audio");
        setStatus(STATUS.IDLE);
        stopHighlightTimer();
        stopKeepAlive();
      }
    };
    utteranceRef.current = utterance;
    synthRef.current.speak(utterance);
  };

  const handlePlay = () => {
    if (!text.trim()) { toast.error("Digite ou grave um texto primeiro"); return; }
    if (status === STATUS.PAUSED) {
      synthRef.current.resume();
      setStatus(STATUS.PLAYING);
      const rate = speed[0];
      const remaining = wordsRef.current.slice(highlightWordIndex);
      if (remaining.length > 0) {
        const msPerWord = (60000 / (150 * rate));
        let wordIdx = highlightWordIndex;
        highlightTimerRef.current = setInterval(() => {
          wordIdx++;
          if (wordIdx >= wordsRef.current.length) { clearInterval(highlightTimerRef.current); highlightTimerRef.current = null; return; }
          setHighlightWordIndex(wordIdx);
        }, msPerWord);
      }
      toast.success("Reproducao retomada");
      return;
    }
    synthRef.current.cancel();
    stopHighlightTimer();
    stopKeepAlive();

    // Split text into chunks for reliable playback
    chunksRef.current = splitIntoChunks(text);
    chunkIndexRef.current = 0;

    setStatus(STATUS.PLAYING);
    startHighlightTimer(text, speed[0]);
    speakChunk(0);
    toast.success("Reproduzindo texto");
  };

  const handlePause = () => {
    if (synthRef.current && status === STATUS.PLAYING) {
      synthRef.current.pause();
      setStatus(STATUS.PAUSED);
      stopKeepAlive();
      if (highlightTimerRef.current) { clearInterval(highlightTimerRef.current); highlightTimerRef.current = null; }
      toast.info("Reproducao pausada");
    }
  };

  const handleStop = () => {
    if (synthRef.current) synthRef.current.cancel();
    chunksRef.current = [];
    chunkIndexRef.current = 0;
    setStatus(STATUS.IDLE);
    stopHighlightTimer();
    stopKeepAlive();
    toast.info("Reproducao parada");
  };

  // ===================== STT FUNCTIONS =====================

  const handleStartRecording = () => {
    if (!isSupported.stt) { toast.error("Seu navegador não suporta gravação de voz"); return; }
    try {
      recognitionRef.current.lang = voiceLang;
      recognitionRef.current.start();
      setStatus(STATUS.RECORDING);
      toast.success("Gravação iniciada - fale agora");
    } catch (error) {
      console.error("Error starting recognition:", error);
      toast.error("Erro ao iniciar gravação.");
    }
  };

  const handleStopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setStatus(STATUS.IDLE);
      toast.info("Gravação finalizada");
    }
  };

  const handleToggleRecording = () => {
    if (status === STATUS.RECORDING) {
      handleStopRecording();
    } else {
      if (status === STATUS.PLAYING || status === STATUS.PAUSED) handleStop();
      handleStartRecording();
    }
  };

  const handleClear = () => {
    if (status === STATUS.PLAYING || status === STATUS.PAUSED) handleStop();
    if (status === STATUS.RECORDING) handleStopRecording();
    setText("");
    toast.success("Texto limpo");
  };

  // ===================== IDEAS FUNCTIONS =====================

  const handleOpenSaveDialog = () => {
    if (!text.trim()) { toast.error("Digite ou grave um texto antes de salvar"); return; }
    setIdeaTitle("");
    setIdeaCategory("Pessoal");
    setShowSaveDialog(true);
    setTimeout(() => titleInputRef.current?.focus(), 100);
  };

  const handleSaveIdea = async () => {
    if (!ideaTitle.trim()) { toast.error("Digite um título para a ideia"); return; }
    setSavingIdea(true);
    try {
      await createIdea(ideaTitle.trim(), text.trim(), ideaCategory);
      setShowSaveDialog(false);
      setIdeaTitle("");
      toast.success("Ideia salva com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar ideia");
    } finally {
      setSavingIdea(false);
    }
  };

  const handleLoadIdea = (idea) => {
    setText(idea.content);
    setShowIdeasPanel(false);
    toast.success(`Ideia "${idea.title}" carregada`);
  };

  const handleOpenEditDialog = (idea) => {
    setEditingIdea(idea);
    setIdeaTitle(idea.title);
    setEditContent(idea.content);
    setIdeaCategory(idea.category || "Pessoal");
    setShowEditDialog(true);
  };

  const handleUpdateIdea = async () => {
    if (!ideaTitle.trim()) { toast.error("Digite um título para a ideia"); return; }
    if (!editContent.trim()) { toast.error("Digite o conteúdo da ideia"); return; }
    setSavingIdea(true);
    try {
      await updateIdea(editingIdea.id, { title: ideaTitle.trim(), content: editContent.trim(), category: ideaCategory });
      setShowEditDialog(false);
      setEditingIdea(null);
      setIdeaTitle("");
      setEditContent("");
      toast.success("Ideia atualizada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar ideia");
    } finally {
      setSavingIdea(false);
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    try {
      await deleteIdea(ideaId);
      setDeleteConfirmId(null);
      toast.success("Ideia excluída");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir ideia");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  // ===================== IMPORT =====================

  const handleImportDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "txt"].includes(ext)) { toast.error("Formato não suportado. Use PDF ou TXT."); return; }
    setImporting(true);
    try {
      if (ext === "txt") {
        const reader = new FileReader();
        reader.onload = (ev) => { setText(ev.target.result || ""); toast.success(`"${file.name}" importado com sucesso`); setImporting(false); };
        reader.onerror = () => { toast.error("Erro ao ler o arquivo"); setImporting(false); };
        reader.readAsText(file);
      } else {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${BACKEND_URL}/api/documents/extract`, formData);
        setText(response.data.text);
        toast.success(`"${file.name}" importado com sucesso`);
        setImporting(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao importar documento");
      setImporting(false);
    }
  };

  // ===================== SHARE =====================

  const handleShareIdea = (idea) => {
    if (isLocal) {
      const preview = idea.content.substring(0, 120);
      const text = `${idea.title}\n\n${preview}${idea.content.length > 120 ? "..." : ""}\n\nCompartilhado via Audio Ideias`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      return;
    }
    setSharingIdeaId(idea.id);
    setShareStep("config");
    setShareUrl("");
    setShareAllowSave(true);
    setShareExpiration("none");
    setShareRecipient("");
    setShareAttachments([]);
  };

  const handleConfirmShare = async () => {
    setShareStep("loading");
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/ideas/${sharingIdeaId}/share`,
        {
          allow_save: shareAllowSave,
          expiration_type: shareExpiration,
          recipient_name: shareRecipient || null,
          attachment_ids: shareAttachments.map(a => a.id),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShareUrl(`${window.location.origin}/compartilhado/${res.data.token}`);
      setShareCopied(false);
      setShareStep("ready");
    } catch { toast.error("Erro ao compartilhar ideia"); setSharingIdeaId(null); }
  };

  const handleShareFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingFiles(true);
    const newAttachments = [...shareAttachments];
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} é muito grande (max 50MB)`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        newAttachments.push(res.data);
      } catch {
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
    setShareAttachments(newAttachments);
    setUploadingFiles(false);
    e.target.value = "";
  };

  const removeShareAttachment = (idx) => {
    setShareAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setShareCopied(false), 2000);
    } catch { toast.error("Erro ao copiar link"); }
  };

  const handleShareWhatsApp = (idea) => {
    const preview = idea.content.substring(0, 120);
    const text = `*${idea.title}*\n\n${preview}${idea.content.length > 120 ? "..." : ""}\n\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  // ===================== SHARED LINKS =====================

  const VIEWS_KEY = "audio_ideias_last_views";

  const fetchSharedLinks = async () => {
    if (!isAuthenticated || !token) return;
    setLoadingShares(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/shares`, { headers: { Authorization: `Bearer ${token}` } });
      setSharedLinks(res.data);
      const totalViews = res.data.reduce((sum, s) => sum + (s.views || 0), 0);
      const lastSeen = parseInt(localStorage.getItem(VIEWS_KEY) || "0", 10);
      setNewViewsCount(Math.max(totalViews - lastSeen, 0));
    } catch { toast.error("Erro ao carregar links compartilhados"); }
    finally { setLoadingShares(false); }
  };

  const markViewsAsSeen = () => {
    const totalViews = sharedLinks.reduce((sum, s) => sum + (s.views || 0), 0);
    localStorage.setItem(VIEWS_KEY, String(totalViews));
    setNewViewsCount(0);
  };

  const handleRevokeShare = async (shareToken) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/shares/${shareToken}`, { headers: { Authorization: `Bearer ${token}` } });
      setSharedLinks((prev) => prev.map((s) => s.token === shareToken ? { ...s, revoked: true } : s));
      toast.success("Link revogado com sucesso");
    } catch { toast.error("Erro ao revogar link"); }
  };

  // ===================== EXPORT =====================

  const handleExportTxt = (idea) => {
    const lines = [idea.title, "", `Categoria: ${idea.category || "Sem categoria"}`, `Data: ${formatDate(idea.created_at)}`, "---", "", idea.content];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const slug = idea.title.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "").slice(0, 50);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${slug || "ideia"}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Exportado como TXT");
  };

  const handleExportPdf = async (idea) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/documents/export-pdf`, { title: idea.title, content: idea.content, category: idea.category, created_at: idea.created_at }, { responseType: "blob" });
      const slug = idea.title.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "").slice(0, 50);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${slug || "ideia"}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Exportado como PDF");
    } catch { toast.error("Erro ao exportar PDF"); }
  };

  // ===================== LAYOUT & AUTH =====================

  const toggleLayoutMode = async () => {
    const newMode = layoutMode === "panel" ? "split" : "panel";
    setLayoutMode(newMode);
    if (newMode === "split") setShowIdeasPanel(false);
    if (isAuthenticated && token) {
      try {
        await axios.put(`${BACKEND_URL}/api/user/layout`, { layout_mode: newMode }, { headers: { Authorization: `Bearer ${token}` } });
      } catch { /* ignore */ }
    }
  };

  // Handle logout — close all panels, navigate, THEN clear auth
  const handleLogout = () => {
    // Close any open portals first
    setShowIdeasPanel(false);
    setShowSharesPanel(false);
    setShowSaveDialog(false);
    setShowEditDialog(false);
    setSharingIdeaId(null);
    // Navigate away
    navigate("/");
    // Clear auth state on next tick (after navigation completes)
    setTimeout(() => {
      logout();
    }, 0);
    toast.success("Você saiu da sua conta");
  };

  // Idea playback
  const handlePlayIdea = (idea) => {
    if (playingIdeaId === idea.id) { synthRef.current.cancel(); setPlayingIdeaId(null); stopKeepAlive(); return; }
    synthRef.current.cancel();
    stopKeepAlive();
    if (status === STATUS.PLAYING || status === STATUS.PAUSED) setStatus(STATUS.IDLE);
    
    const chunks = splitIntoChunks(idea.content);
    let idx = 0;
    const speakNext = () => {
      if (idx >= chunks.length) { setPlayingIdeaId(null); stopKeepAlive(); return; }
      const utt = new SpeechSynthesisUtterance(chunks[idx]);
      utt.rate = speed[0];
      utt.lang = voiceLang;
      const v = voices.find((vc) => vc.name === selectedVoice);
      if (v) utt.voice = v;
      utt.onend = () => { idx++; speakNext(); };
      utt.onerror = (e) => { if (e.error !== "interrupted" && e.error !== "cancelled") setPlayingIdeaId(null); };
      synthRef.current.speak(utt);
    };
    speakNext();
    setPlayingIdeaId(idea.id);
  };

  // ===================== COMPUTED =====================

  const userStatusInfo = getStatusInfo();

  const getVoiceStatusInfo = () => {
    switch (status) {
      case STATUS.RECORDING: return { text: "Gravando", color: "text-rose-500", bgColor: "bg-rose-500/10" };
      case STATUS.PLAYING: return { text: "Reproduzindo", color: "text-emerald-500", bgColor: "bg-emerald-500/10" };
      case STATUS.PAUSED: return { text: "Pausado", color: "text-amber-500", bgColor: "bg-amber-500/10" };
      default: return { text: "Pronto", color: "text-muted-foreground", bgColor: "bg-muted" };
    }
  };

  const voiceStatusInfo = getVoiceStatusInfo();

  const filteredIdeas = ideas.filter((idea) => {
    if (filterFavorites && !idea.favorite) return false;
    if (filterCategory && idea.category !== filterCategory) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return idea.title.toLowerCase().includes(q) || idea.content.toLowerCase().includes(q) || (idea.category && idea.category.toLowerCase().includes(q));
    }
    return true;
  });

  // Shared props for IdeasPanel
  const ideasPanelProps = {
    ideas,
    filteredIdeas,
    ideasLoading,
    searchQuery,
    setSearchQuery,
    filterCategory,
    setFilterCategory,
    filterFavorites,
    setFilterFavorites,
    CATEGORIES,
    isLocal,
    isAuthenticated,
    playingIdeaId,
    deleteConfirmId,
    setDeleteConfirmId,
    onPlay: handlePlayIdea,
    onShare: handleShareIdea,
    onLoad: handleLoadIdea,
    onEdit: handleOpenEditDialog,
    onDelete: handleDeleteIdea,
    onFavorite: toggleFavorite,
    onExportTxt: handleExportTxt,
    onExportPdf: handleExportPdf,
    onSend: (id, title) => { setSendToUserIdeaId(id); setSendToUserIdeaTitle(title); },
    formatDate,
  };

  // ===================== RENDER =====================

  return (
    <TooltipProvider delayDuration={300}>
      <div className="h-screen flex flex-col overflow-hidden transition-colors duration-300" style={{ background: 'var(--vi-page)', width: '100%' }} data-testid="voice-ideias-container">
        {/* Promotional Strip Banner (top of page, subtle) */}
        <PromoBanner />

        {/* Hidden file input */}
        <input ref={fileInputRef} type="file" accept=".pdf,.txt" onChange={handleImportDocument} className="hidden" data-testid="import-file-input" />

        {/* Focus Mode Overlay */}
        {focusMode && (
          <div className="fixed inset-0 z-50 flex flex-col transition-opacity duration-300" style={{ background: 'var(--vi-page)' }} data-testid="focus-mode-overlay">
            <div className="flex items-center justify-between px-6 py-3">
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: 'var(--vi-text-muted)', opacity: 0.5 }}>Modo Foco</span>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2.5 py-1 rounded-full ${status === STATUS.RECORDING ? "bg-rose-500/20 text-rose-400" : status === STATUS.PLAYING ? "bg-emerald-500/20 text-emerald-400" : ""}`} style={status === STATUS.IDLE ? { color: 'var(--vi-text-muted)', opacity: 0.4 } : {}}>
                  {status === STATUS.RECORDING ? "Gravando..." : status === STATUS.PLAYING ? "Reproduzindo..." : status === STATUS.PAUSED ? "Pausado" : ""}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setFocusMode(false)} className="h-8 px-3 text-xs gap-1.5 rounded-full" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text-muted)' }} data-testid="exit-focus-mode">
                  <X className="w-3.5 h-3.5" /> ESC
                </Button>
              </div>
            </div>
            <div className="flex-1 flex flex-col px-6 md:px-16 lg:px-32 xl:px-48 pb-6 relative">
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva suas ideias..." autoFocus className="flex-1 w-full text-lg md:text-xl lg:text-2xl leading-relaxed resize-none bg-transparent border-none outline-none focus:ring-0" style={{ color: 'var(--vi-text-soft)' }} data-testid="focus-textarea" />
              {status === STATUS.PLAYING && highlightWordIndex >= 0 && renderHighlightedText(text, "text-lg md:text-xl lg:text-2xl leading-relaxed", {})}
              <div className="flex items-center justify-center gap-4 pt-4" style={{ borderTop: '1px solid var(--vi-border)' }}>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handleToggleRecording} className={`h-12 w-12 rounded-full transition-all ${status === STATUS.RECORDING ? "bg-rose-500/20 border-2 border-rose-500/50 text-rose-400 animate-pulse" : "border border-white/10 hover:bg-white/5"}`} style={status !== STATUS.RECORDING ? { color: 'var(--vi-text-muted)' } : {}} disabled={!isSupported.stt} data-testid="focus-mic-button">
                    {status === STATUS.RECORDING ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </Button>
                </TooltipTrigger><TooltipContent>{status === STATUS.RECORDING ? "Parar gravação" : "Gravar voz"}</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={status === STATUS.PLAYING ? handleStop : handlePlay} className={`h-12 w-12 rounded-full transition-all border ${status === STATUS.PLAYING ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400" : "border-white/10 hover:bg-white/5"}`} style={status !== STATUS.PLAYING ? { color: 'var(--vi-text-muted)' } : {}} disabled={!text.trim() || !isSupported.tts || status === STATUS.RECORDING} data-testid="focus-play-button">
                    {status === STATUS.PLAYING ? <Square className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </Button>
                </TooltipTrigger><TooltipContent>{status === STATUS.PLAYING ? "Parar" : "Ouvir"}</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => { setFocusMode(false); setShowSaveDialog(true); }} className="h-12 w-12 rounded-full border border-white/10 hover:bg-white/5" style={{ color: 'var(--vi-text-muted)' }} disabled={!text.trim()} data-testid="focus-save-button">
                    <Save className="w-5 h-5" />
                  </Button>
                </TooltipTrigger><TooltipContent>Salvar como ideia</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handleClear} className="h-12 w-12 rounded-full border border-white/10 hover:bg-white/5" style={{ color: 'var(--vi-text-muted)' }} disabled={!text.trim()} data-testid="focus-clear-button">
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </TooltipTrigger><TooltipContent>Limpar texto</TooltipContent></Tooltip>
              </div>
              <p className="text-center text-xs mt-3" style={{ color: 'var(--vi-text-muted)', opacity: 0.4 }}>{text.length} caracteres</p>
            </div>
          </div>
        )}

        {/* ===== HEADER (extracted component) ===== */}
        <AppHeader
          user={user}
          isAuthenticated={isAuthenticated}
          userStatusInfo={userStatusInfo}
          status={status}
          voiceStatusInfo={voiceStatusInfo}
          layoutMode={layoutMode}
          ideasCount={ideas.length}
          newViewsCount={newViewsCount}
          unreadMessages={unreadMessages}
          isDark={isDark}
          onToggleLayout={toggleLayoutMode}
          onOpenIdeas={() => setShowIdeasPanel(true)}
          onOpenShares={() => { setShowSharesPanel(true); fetchSharedLinks(); }}
          onOpenFocus={() => setFocusMode(true)}
          onToggleTheme={toggleTheme}
          onLogout={handleLogout}
          onOpenMessages={() => navigate("/mensagens")}
          onStartTutorial={() => setShowWelcomeTour(true)}
        />

        {/* ===== MAIN CONTENT ===== */}
        <div className={`flex-1 flex ${layoutMode === "split" ? "flex-row" : "flex-col"} overflow-hidden`}>
          <main className={`flex-1 min-w-0 flex flex-col px-4 pt-[5vh] md:pt-[8vh] pb-4 md:px-8 md:pb-6 gap-4 overflow-y-auto ${layoutMode === "split" ? "lg:px-6" : "w-full lg:px-12 max-w-4xl mx-auto"}`}>
            <div className="relative">
              <div className="glass-card rounded-2xl p-4 md:p-6 relative animate-fade-in-up">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Digite ou fale para capturar suas ideias..."
                  className="ghost-textarea w-full text-base md:text-lg lg:text-xl leading-relaxed resize-none"
                  style={{ minHeight: "28vh", maxHeight: "50vh", color: 'var(--vi-text-soft)' }}
                  data-testid="main-textarea"
                />
                {status === STATUS.PLAYING && highlightWordIndex >= 0 && renderHighlightedText(text, "text-base md:text-lg lg:text-xl leading-relaxed p-4 md:p-6", { minHeight: "28vh", maxHeight: "50vh" })}
                {status === STATUS.RECORDING && (
                  <div className="absolute bottom-20 right-4 md:bottom-24 md:right-6 flex items-center gap-1 transition-opacity duration-200" data-testid="audio-visualizer">
                    {[...Array(5)].map((_, i) => (<div key={i} className="wave-bar w-1 bg-rose-500 rounded-full" style={{ height: "24px" }} />))}
                  </div>
                )}
                <div className="text-xs font-mono mt-2 mb-4" style={{ color: 'var(--vi-text-muted)' }} data-testid="char-count">{text.length} caracteres</div>
                <div className="h-px w-full mb-4" style={{ background: 'var(--vi-border-accent)' }} />

                {/* Mobile Controls */}
                <div className="flex flex-col gap-5 md:hidden">
                  <div className="flex justify-center">
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={handleToggleRecording} disabled={!isSupported.stt} className={`h-20 w-20 rounded-full flex items-center justify-center transition-all duration-300 touch-target active:scale-95 ${status === STATUS.RECORDING ? "bg-rose-500/20 border-2 border-rose-500 recording-pulse glow-record" : "bg-rose-500/10 border-2 border-rose-500/50 hover:bg-rose-500/20"} disabled:opacity-50 disabled:cursor-not-allowed`} data-testid="record-button">
                        {status === STATUS.RECORDING ? <MicOff className="w-8 h-8 text-rose-500" /> : <Mic className="w-8 h-8 text-rose-500" />}
                      </button>
                    </TooltipTrigger><TooltipContent>{status === STATUS.RECORDING ? "Parar gravação" : "Iniciar gravação"}</TooltipContent></Tooltip>
                  </div>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handlePlay} disabled={!isSupported.tts || status === STATUS.RECORDING} className={`h-11 w-11 rounded-full ${status === STATUS.PLAYING ? "bg-emerald-500/20 border border-emerald-500 playing-pulse glow-play" : "bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20"} touch-target`} data-testid="play-button"><Play className="w-4 h-4 text-emerald-500" /></Button></TooltipTrigger><TooltipContent>Reproduzir</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handlePause} disabled={status !== STATUS.PLAYING} className="h-11 w-11 rounded-full bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 disabled:opacity-30 touch-target" data-testid="pause-button"><Pause className="w-4 h-4 text-amber-500" /></Button></TooltipTrigger><TooltipContent>Pausar</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handleStop} disabled={status === STATUS.IDLE || status === STATUS.RECORDING} className="h-11 w-11 rounded-full border disabled:opacity-30 touch-target" style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)' }} data-testid="stop-button"><Square className="w-4 h-4" style={{ color: 'var(--vi-text-soft)' }} /></Button></TooltipTrigger><TooltipContent>Parar</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handleClear} className="h-11 w-11 rounded-full border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 touch-target" data-testid="clear-button"><Trash2 className="w-4 h-4 text-rose-400" /></Button></TooltipTrigger><TooltipContent>Apagar texto</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handleOpenSaveDialog} className="h-11 w-11 rounded-full bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20 touch-target" data-testid="save-button"><Save className="w-4 h-4 text-indigo-500" /></Button></TooltipTrigger><TooltipContent>Salvar ideia</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} disabled={importing} className="h-11 w-11 rounded-full bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 touch-target" data-testid="import-button">{importing ? <Loader2 className="w-4 h-4 text-cyan-500 animate-spin" /> : <FileUp className="w-4 h-4 text-cyan-500" />}</Button></TooltipTrigger><TooltipContent>Importar documento</TooltipContent></Tooltip>
                  </div>
                  {/* Mobile voice selectors */}
                  <div className="flex items-center gap-3 rounded-lg px-3 py-2" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                    <select value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)} className="flex-1 h-8 bg-transparent text-sm outline-none cursor-pointer" style={{ color: 'var(--vi-text)' }} data-testid="voice-lang-select">
                      {availableLanguages.map((lang) => (<option key={lang} value={lang} style={{ color: '#000', background: '#fff' }}>{lang}</option>))}
                      {availableLanguages.length === 0 && <option value="pt-BR" style={{ color: '#000', background: '#fff' }}>pt-BR</option>}
                    </select>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg px-3 py-2" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <Languages className="w-4 h-4 text-muted-foreground shrink-0" />
                    <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)} className="flex-1 h-8 bg-transparent text-sm outline-none cursor-pointer" style={{ color: 'var(--vi-text)' }} data-testid="voice-select">
                      {filteredVoices.map((voice) => (<option key={voice.name} value={voice.name} style={{ color: '#000', background: '#fff' }}>{voice.name}</option>))}
                      {filteredVoices.length === 0 && <option value="" disabled style={{ color: '#000', background: '#fff' }}>Nenhuma voz disponível</option>}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-2 rounded-lg px-3 py-2 flex-1" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                      <User className="w-4 h-4 text-muted-foreground shrink-0" />
                      <select value={voiceGender} onChange={(e) => setVoiceGender(e.target.value)} className="flex-1 h-8 bg-transparent text-sm outline-none cursor-pointer" style={{ color: 'var(--vi-text)' }} data-testid="voice-gender-select">
                        <option value="all" style={{ color: '#000', background: '#fff' }}>Qualquer</option>
                        <option value="female" disabled={voices.length > 0 && !availableGenders.female} style={{ color: voices.length > 0 && !availableGenders.female ? '#999' : '#000', background: '#fff' }}>Feminino{voices.length > 0 && !availableGenders.female ? " (indisponível)" : ""}</option>
                        <option value="male" disabled={voices.length > 0 && !availableGenders.male} style={{ color: voices.length > 0 && !availableGenders.male ? '#999' : '#000', background: '#fff' }}>Masculino{voices.length > 0 && !availableGenders.male ? " (indisponível)" : ""}</option>
                      </select>
                    </div>
                    <button onClick={() => { const utt = new SpeechSynthesisUtterance("Teste de voz"); utt.lang = voiceLang; const v = voices.find(v => v.name === selectedVoice); if (v) utt.voice = v; synthRef.current.cancel(); synthRef.current.speak(utt); }} className="h-10 px-3 rounded-lg text-sm shrink-0" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text)' }} data-testid="voice-preview-btn">Testar</button>
                  </div>
                  {genderWarning && (
                    <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs" style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.2)', color: '#eab308' }} data-testid="gender-warning">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{genderWarning}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 rounded-lg px-3 py-2" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <Gauge className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Slider value={speed} onValueChange={setSpeed} min={0.5} max={2} step={0.1} className="flex-1" data-testid="speed-slider" />
                    <span className="text-sm font-mono text-muted-foreground w-10 text-right" data-testid="speed-value">{speed[0].toFixed(1)}x</span>
                  </div>
                </div>

                {/* Desktop Controls */}
                <div className="hidden md:flex md:flex-col md:gap-3" data-testid="desktop-controls-bar">
                  <div className="flex items-center justify-center gap-2.5">
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handleToggleRecording} disabled={!isSupported.stt} className={`h-10 w-10 rounded-full shrink-0 ${status === STATUS.RECORDING ? "bg-rose-500/20 border border-rose-500 recording-pulse glow-record" : "bg-rose-500/10 border border-rose-500/50 hover:bg-rose-500/20"} disabled:opacity-50`} data-testid="record-button-desktop">{status === STATUS.RECORDING ? <MicOff className="w-4.5 h-4.5 text-rose-500" /> : <Mic className="w-4.5 h-4.5 text-rose-500" />}</Button></TooltipTrigger><TooltipContent>{status === STATUS.RECORDING ? "Parar gravação" : "Iniciar gravação"}</TooltipContent></Tooltip>
                    <div className="h-6 w-px shrink-0" style={{ background: 'var(--vi-border-accent)' }} />
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handlePlay} disabled={!isSupported.tts || status === STATUS.RECORDING} className={`h-10 w-10 rounded-full ${status === STATUS.PLAYING ? "bg-emerald-500/20 border border-emerald-500 playing-pulse glow-play" : "bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20"}`} data-testid="play-button-desktop"><Play className="w-4 h-4 text-emerald-500" /></Button></TooltipTrigger><TooltipContent>Reproduzir</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handlePause} disabled={status !== STATUS.PLAYING} className="h-10 w-10 rounded-full bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 disabled:opacity-30" data-testid="pause-button-desktop"><Pause className="w-4 h-4 text-amber-500" /></Button></TooltipTrigger><TooltipContent>Pausar</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handleStop} disabled={status === STATUS.IDLE || status === STATUS.RECORDING} className="h-10 w-10 rounded-full border disabled:opacity-30" style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)' }} data-testid="stop-button-desktop"><Square className="w-4 h-4" style={{ color: 'var(--vi-text-soft)' }} /></Button></TooltipTrigger><TooltipContent>Parar</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={handleClear} className="h-10 w-10 rounded-full border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20" data-testid="clear-button-desktop"><Trash2 className="w-4 h-4 text-rose-400" /></Button></TooltipTrigger><TooltipContent>Apagar texto</TooltipContent></Tooltip>
                    <div className="h-6 w-px shrink-0" style={{ background: 'var(--vi-border-accent)' }} />
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" onClick={handleOpenSaveDialog} className="h-10 gap-2 rounded-full bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20 text-indigo-400 px-4" data-testid="save-button-desktop"><Save className="w-4 h-4" />Salvar</Button></TooltipTrigger><TooltipContent>Salvar ideia atual</TooltipContent></Tooltip>
                    <Tooltip><TooltipTrigger asChild><Button variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={importing} className="h-10 gap-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 text-cyan-400 px-4" data-testid="import-button-desktop">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}Importar</Button></TooltipTrigger><TooltipContent>Importar documento (PDF ou TXT)</TooltipContent></Tooltip>
                  </div>
                  {/* Desktop voice bar */}
                  <div className="flex items-center h-10 rounded-xl overflow-hidden w-full" style={{ border: '1px solid var(--vi-border-accent)' }} data-testid="voice-speed-container">
                    <div className="flex items-center gap-2 px-3 h-full shrink-0" style={{ background: 'var(--vi-input-bg)' }}>
                      <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                      <select value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)} className="h-8 w-20 bg-transparent text-sm outline-none cursor-pointer" style={{ color: 'var(--vi-text)' }} data-testid="voice-lang-select-desktop">
                        {availableLanguages.map((lang) => (<option key={lang} value={lang} style={{ color: '#000', background: '#fff' }}>{lang}</option>))}
                        {availableLanguages.length === 0 && <option value="pt-BR" style={{ color: '#000', background: '#fff' }}>pt-BR</option>}
                      </select>
                    </div>
                    <div className="w-px self-stretch shrink-0" style={{ background: 'var(--vi-border-accent)' }} />
                    <div className="flex items-center gap-2 px-3 h-full flex-1 min-w-0" style={{ background: 'var(--vi-input-bg)' }}>
                      <Languages className="w-4 h-4 text-muted-foreground shrink-0" />
                      <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)} className="h-8 flex-1 min-w-0 bg-transparent text-sm outline-none cursor-pointer truncate" style={{ color: 'var(--vi-text)' }} data-testid="voice-select-desktop">
                        {filteredVoices.map((voice) => (<option key={voice.name} value={voice.name} style={{ color: '#000', background: '#fff' }}>{voice.name}</option>))}
                        {filteredVoices.length === 0 && <option value="" disabled style={{ color: '#000', background: '#fff' }}>Nenhuma voz</option>}
                      </select>
                    </div>
                    <div className="w-px self-stretch shrink-0" style={{ background: 'var(--vi-border-accent)' }} />
                    <div className="flex items-center gap-1.5 px-2.5 h-full shrink-0" style={{ background: 'var(--vi-input-bg)' }}>
                      <select value={voiceGender} onChange={(e) => setVoiceGender(e.target.value)} className="h-8 w-24 bg-transparent text-sm outline-none cursor-pointer" style={{ color: 'var(--vi-text)' }} data-testid="voice-gender-select-desktop">
                        <option value="all" style={{ color: '#000', background: '#fff' }}>Qualquer</option>
                        <option value="female" disabled={voices.length > 0 && !availableGenders.female} style={{ color: voices.length > 0 && !availableGenders.female ? '#999' : '#000', background: '#fff' }}>Feminino{voices.length > 0 && !availableGenders.female ? " (indisponível)" : ""}</option>
                        <option value="male" disabled={voices.length > 0 && !availableGenders.male} style={{ color: voices.length > 0 && !availableGenders.male ? '#999' : '#000', background: '#fff' }}>Masculino{voices.length > 0 && !availableGenders.male ? " (indisponível)" : ""}</option>
                      </select>
                    </div>
                    <div className="w-px self-stretch shrink-0" style={{ background: 'var(--vi-border-accent)' }} />
                    <button onClick={() => { const utt = new SpeechSynthesisUtterance("Teste de voz"); utt.lang = voiceLang; const v = voices.find(v => v.name === selectedVoice); if (v) utt.voice = v; synthRef.current.cancel(); synthRef.current.speak(utt); }} className="h-full px-3 text-xs shrink-0 hover:opacity-80" style={{ background: 'var(--vi-input-bg)', color: 'var(--vi-text-soft)' }} data-testid="voice-preview-btn-desktop">Testar</button>
                    <div className="w-px self-stretch shrink-0" style={{ background: 'var(--vi-border-accent)' }} />
                    <div className="flex items-center gap-2.5 px-3 h-full shrink-0" style={{ background: 'var(--vi-input-bg)' }}>
                      <Gauge className="w-4 h-4 text-muted-foreground shrink-0" />
                      <Slider value={speed} onValueChange={setSpeed} min={0.5} max={2} step={0.1} className="w-24" data-testid="speed-slider-desktop" />
                      <span className="text-xs font-mono text-muted-foreground w-8 text-right tabular-nums" data-testid="speed-value-desktop">{speed[0].toFixed(1)}x</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {genderWarning && (
              <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs mt-2 max-w-2xl mx-auto" style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.2)', color: '#eab308' }} data-testid="gender-warning-desktop">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{genderWarning}</span>
              </div>
            )}
            {(!isSupported.tts || !isSupported.stt) && (
              <div className="text-center text-sm text-amber-500/80 animate-fade-in-up">
                {!isSupported.tts && !isSupported.stt && <p>Seu navegador não suporta as APIs de voz. Tente usar Chrome ou Edge.</p>}
                {!isSupported.tts && isSupported.stt && <p>Seu navegador não suporta Text-to-Speech.</p>}
                {isSupported.tts && !isSupported.stt && <p>Seu navegador não suporta Speech-to-Text.</p>}
              </div>
            )}
          </main>

          {/* Split Mode: Fixed Ideas Sidebar */}
          {layoutMode === "split" && (
            <aside className="hidden lg:flex flex-col w-[340px] xl:w-[400px] shrink-0 overflow-y-auto" style={{ borderLeft: '1px solid var(--vi-border)', background: 'var(--vi-card-solid)' }} data-testid="split-ideas-panel">
              <IdeasPanel {...ideasPanelProps} inSplit={true} />
            </aside>
          )}
        </div>

        {/* Footer */}
        <footer className="px-4 py-2.5 md:px-8 lg:px-12 md:py-3 flex items-center gap-4" style={{ borderTop: '1px solid var(--vi-border)' }}>
          <a href="/admin/login" className="text-xs shrink-0 hover:underline transition-all" style={{ color: 'var(--vi-text-soft)' }} data-testid="admin-link">Admin</a>
          <p className="text-center text-xs flex-1" style={{ color: 'var(--vi-text-muted)' }}>Audio Ideias - Capture suas ideias por voz ou texto</p>
        </footer>

        {/* ===== PORTALS: Sheets & Dialogs (isolated from header) ===== */}

        {/* Ideas Sheet (panel mode) */}
        <Sheet open={showIdeasPanel} onOpenChange={(open) => {
          setShowIdeasPanel(open);
          if (!open) { setSearchQuery(""); setFilterFavorites(false); if (playingIdeaId && synthRef.current) { synthRef.current.cancel(); setPlayingIdeaId(null); } }
        }}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" style={{ background: 'var(--vi-card-solid)', borderLeft: '1px solid var(--vi-border-accent)' }}>
            <SheetHeader>
              <SheetTitle className="font-heading flex items-center gap-2" style={{ color: 'var(--vi-text)' }}>
                Minhas Ideias
                {isLocal ? (<span className="text-xs font-normal px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1"><HardDrive className="w-3 h-3" />Local</span>) : (<span className="text-xs font-normal px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 flex items-center gap-1"><Cloud className="w-3 h-3" />Sincronizado</span>)}
              </SheetTitle>
              <SheetDescription>
                {ideas.length === 0 ? "Você ainda não salvou nenhuma ideia" : searchQuery.trim() || filterCategory ? `${filteredIdeas.length} de ${ideas.length} ${ideas.length === 1 ? 'ideia' : 'ideias'}` : `${ideas.length} ${ideas.length === 1 ? 'ideia salva' : 'ideias salvas'}`}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              <IdeasPanel {...ideasPanelProps} inSplit={false} />
            </div>
          </SheetContent>
        </Sheet>

        {/* Shares Sheet */}
        <Sheet open={showSharesPanel} onOpenChange={(open) => { setShowSharesPanel(open); if (!open) markViewsAsSeen(); }}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" style={{ background: 'var(--vi-card-solid)', borderLeft: '1px solid var(--vi-border-accent)' }}>
            <SheetHeader>
              <SheetTitle className="font-heading flex items-center gap-2" style={{ color: 'var(--vi-text)' }}>Links Compartilhados</SheetTitle>
              <SheetDescription>{sharedLinks.length} link(s) compartilhado(s)</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-3" data-testid="shares-panel-list">
              {loadingShares ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
              ) : sharedLinks.length === 0 ? (
                <div className="text-center py-12">
                  <Link2 className="w-8 h-8 mx-auto mb-3 opacity-20" style={{ color: 'var(--vi-text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--vi-text-muted)' }}>Nenhum link compartilhado</p>
                </div>
              ) : (
                sharedLinks.map((share) => {
                  const isActive = !share.revoked && !share.expired;
                  return (
                    <div key={share.token} className="rounded-xl p-4 transition-all" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', opacity: isActive ? 1 : 0.6 }} data-testid={`share-item-${share.token}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="text-sm font-medium truncate" style={{ color: 'var(--vi-text)' }}>{share.title}</h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 font-medium ${share.revoked ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : share.expired ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"}`}>
                          {share.revoked ? "Revogado" : share.expired ? "Expirado" : "Ativo"}
                        </span>
                      </div>
                      {share.recipient_name && (<p className="text-xs mb-2 flex items-center gap-1.5" style={{ color: 'var(--vi-text-soft)' }}><User className="w-3 h-3" style={{ color: 'var(--vi-text-muted)' }} />Enviado para: <span className="font-medium">{share.recipient_name}</span></p>)}
                      <div className="flex items-center gap-3 text-xs mb-3" style={{ color: 'var(--vi-text-muted)' }}>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{share.views || 0} {(share.views || 0) === 1 ? "visualização" : "visualizações"}</span>
                        <span>{new Date(share.shared_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}</span>
                        {share.allow_save && <span className="text-indigo-400">Salvar ativo</span>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/compartilhado/${share.token}`); toast.success("Link copiado!"); }} disabled={!isActive} className="flex-1 justify-center gap-1.5 text-xs" style={{ background: isActive ? 'var(--vi-card-solid)' : 'transparent' }} data-testid={`copy-share-${share.token}`}><Copy className="w-3 h-3" />Copiar</Button>
                        {isActive && (<Button size="sm" variant="ghost" onClick={() => { const url = `${window.location.origin}/compartilhado/${share.token}`; const preview = share.content?.substring(0, 80) || ""; const t = `*${share.title}*\n\n${preview}${preview.length >= 80 ? "..." : ""}\n\n${url}`; window.open(`https://wa.me/?text=${encodeURIComponent(t)}`, "_blank"); }} className="flex-1 justify-center gap-1.5 text-xs text-[#25D366] hover:bg-[#25D366]/10" data-testid={`whatsapp-share-${share.token}`}>Reenviar</Button>)}
                        {isActive && (<Button size="sm" variant="ghost" onClick={() => handleRevokeShare(share.token)} className="flex-1 justify-center gap-1.5 text-xs text-rose-400 hover:bg-rose-500/10" data-testid={`revoke-share-${share.token}`}><Ban className="w-3 h-3" />Revogar</Button>)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Save Idea Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md" style={{ background: 'var(--vi-card-solid)', border: '1px solid var(--vi-border-accent)' }}>
            <DialogHeader><DialogTitle className="font-heading" style={{ color: 'var(--vi-text)' }}>Salvar Ideia</DialogTitle><DialogDescription>Dê um título e escolha uma categoria para sua ideia.</DialogDescription></DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="idea-title" style={{ color: 'var(--vi-text-soft)' }}>Título</Label>
                <Input id="idea-title" ref={titleInputRef} value={ideaTitle} onChange={(e) => setIdeaTitle(e.target.value)} placeholder="Título da ideia" style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text)' }} onKeyDown={(e) => { if (e.key === "Enter") handleSaveIdea(); }} data-testid="idea-title-input" />
              </div>
              <div className="space-y-2">
                <Label style={{ color: 'var(--vi-text-soft)' }}>Categoria</Label>
                <Select value={ideaCategory} onValueChange={setIdeaCategory}>
                  <SelectTrigger data-testid="idea-category-select" style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)' }}><SelectValue placeholder="Selecionar categoria" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((cat) => (<SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">Prévia: {text.substring(0, 100)}{text.length > 100 ? "..." : ""}</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => setShowSaveDialog(false)} className="border border-white/10" data-testid="cancel-save-button">Cancelar</Button>
              <Button onClick={handleSaveIdea} disabled={savingIdea} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-save-button">{savingIdea ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Salvar</>}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Idea Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-lg" style={{ background: 'var(--vi-card-solid)', border: '1px solid var(--vi-border-accent)' }}>
            <DialogHeader><DialogTitle className="font-heading" style={{ color: 'var(--vi-text)' }}>Editar Ideia</DialogTitle><DialogDescription>Atualize o título, conteúdo ou categoria da ideia.</DialogDescription></DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-idea-title" style={{ color: 'var(--vi-text-soft)' }}>Título</Label>
                <Input id="edit-idea-title" value={ideaTitle} onChange={(e) => setIdeaTitle(e.target.value)} placeholder="Título da ideia" style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)', color: 'var(--vi-text)' }} data-testid="edit-idea-title-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-idea-content" style={{ color: 'var(--vi-text-soft)' }}>Conteúdo</Label>
                <textarea id="edit-idea-content" value={editContent} onChange={(e) => setEditContent(e.target.value)} placeholder="Conteúdo da ideia" rows={5} className="w-full rounded-md px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500/50" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text)' }} data-testid="edit-idea-content-input" />
              </div>
              <div className="space-y-2">
                <Label style={{ color: 'var(--vi-text-soft)' }}>Categoria</Label>
                <Select value={ideaCategory} onValueChange={setIdeaCategory}>
                  <SelectTrigger data-testid="edit-idea-category-select" style={{ background: 'var(--vi-input-bg)', borderColor: 'var(--vi-border-accent)' }}><SelectValue placeholder="Selecionar categoria" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((cat) => (<SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => { setShowEditDialog(false); setEditingIdea(null); }} className="border border-white/10" data-testid="cancel-edit-button">Cancelar</Button>
              <Button onClick={handleUpdateIdea} disabled={savingIdea} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-edit-button">{savingIdea ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Pencil className="w-4 h-4 mr-2" />Atualizar</>}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share Idea Dialog */}
        <Dialog open={!!sharingIdeaId} onOpenChange={(open) => { if (!open) { setSharingIdeaId(null); setShareUrl(""); setShareCopied(false); } }}>
          <DialogContent className="sm:max-w-md" style={{ background: 'var(--vi-card-solid)', border: '1px solid var(--vi-border-accent)' }}>
            <DialogHeader><DialogTitle className="font-heading" style={{ color: 'var(--vi-text)' }}>Compartilhar Ideia</DialogTitle><DialogDescription>{shareStep === "ready" ? "Link pronto! Copie ou envie pelo WhatsApp." : "Configure as opções de compartilhamento."}</DialogDescription></DialogHeader>
            <div className="py-3 space-y-4">
              {shareStep === "config" && (
                <>
                  <div className="rounded-lg p-3" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--vi-text)' }}>Para quem você está enviando?</p>
                    <input type="text" value={shareRecipient} onChange={(e) => setShareRecipient(e.target.value)} placeholder="Ex: João, Maria..." className="w-full h-9 rounded-lg px-3 text-sm" style={{ background: 'var(--vi-card-solid)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text)' }} data-testid="share-recipient-input" />
                    <p className="text-xs mt-1.5" style={{ color: 'var(--vi-text-muted)' }}>Opcional</p>
                  </div>
                  <div className="flex items-center justify-between rounded-lg p-3" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <div><p className="text-sm font-medium" style={{ color: 'var(--vi-text)' }}>Permitir salvar</p><p className="text-xs mt-0.5" style={{ color: 'var(--vi-text-muted)' }}>Quem receber pode salvar como ideia</p></div>
                    <Switch checked={shareAllowSave} onCheckedChange={setShareAllowSave} data-testid="share-allow-save-switch" />
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--vi-text)' }}>Validade do link</p>
                    <Select value={shareExpiration} onValueChange={setShareExpiration}>
                      <SelectTrigger className="w-full h-9" data-testid="share-expiration-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="24h">24 horas</SelectItem><SelectItem value="7d">7 dias</SelectItem><SelectItem value="none">Sem expiração</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)' }}>
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--vi-text)' }}>Anexar arquivos</p>
                    <p className="text-xs mb-2" style={{ color: 'var(--vi-text-muted)' }}>Documentos, imagens, videos (max 50MB cada)</p>
                    {shareAttachments.length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {shareAttachments.map((att, idx) => (
                          <div key={att.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'var(--vi-card-solid)', border: '1px solid var(--vi-border-accent)' }} data-testid={`share-attachment-${idx}`}>
                            <FileText className="w-3.5 h-3.5 shrink-0 text-indigo-400" />
                            <span className="truncate flex-1" style={{ color: 'var(--vi-text-soft)' }}>{att.filename}</span>
                            <span className="text-[10px] shrink-0" style={{ color: 'var(--vi-text-muted)' }}>{(att.size / 1024).toFixed(0)}KB</span>
                            <button onClick={() => removeShareAttachment(idx)} className="shrink-0 p-0.5 rounded hover:bg-rose-500/20 transition-colors" data-testid={`remove-attachment-${idx}`}>
                              <X className="w-3 h-3 text-rose-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg transition-colors hover:bg-indigo-500/10" style={{ border: '1px dashed var(--vi-border-accent)' }}>
                      {uploadingFiles ? <LoaderIcon className="w-4 h-4 animate-spin text-indigo-400" /> : <Paperclip className="w-4 h-4 text-indigo-400" />}
                      <span className="text-xs font-medium" style={{ color: 'var(--vi-text-soft)' }}>{uploadingFiles ? "Enviando..." : "Adicionar arquivo(s)"}</span>
                      <input type="file" multiple onChange={handleShareFileUpload} className="hidden" disabled={uploadingFiles} data-testid="share-file-input" />
                    </label>
                  </div>
                  <Button onClick={handleConfirmShare} disabled={uploadingFiles} className="w-full h-11 rounded-xl gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium" data-testid="share-confirm-button"><Share2 className="w-4 h-4" />Gerar link de compartilhamento</Button>
                </>
              )}
              {shareStep === "loading" && (<div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>)}
              {shareStep === "ready" && (
                <>
                  <div className="flex gap-3 text-xs" style={{ color: 'var(--vi-text-muted)' }}>
                    <span className={`px-2 py-1 rounded-full border ${shareAllowSave ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"}`}>{shareAllowSave ? "Salvar permitido" : "Somente leitura"}</span>
                    <span className="px-2 py-1 rounded-full border bg-indigo-500/10 text-indigo-400 border-indigo-500/20">{shareExpiration === "24h" ? "Expira em 24h" : shareExpiration === "7d" ? "Expira em 7 dias" : "Sem expiração"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input readOnly value={shareUrl} className="flex-1 h-10 rounded-lg px-3 text-sm truncate" style={{ background: 'var(--vi-input-bg)', border: '1px solid var(--vi-border-accent)', color: 'var(--vi-text)' }} data-testid="share-link-input" />
                    <Button size="icon" onClick={handleCopyShareLink} className={`h-10 w-10 shrink-0 rounded-lg ${shareCopied ? "bg-emerald-500/20 text-emerald-400" : "bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20"}`} data-testid="copy-share-link-button">{shareCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
                  </div>
                  <Button onClick={() => { const idea = ideas.find(i => i.id === sharingIdeaId); if (idea) handleShareWhatsApp(idea); }} className="w-full h-11 rounded-xl gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-medium" data-testid="share-whatsapp-button">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Enviar pelo WhatsApp
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Send to User Dialog */}
        <SendToUserDialog ideaId={sendToUserIdeaId} ideaTitle={sendToUserIdeaTitle} open={!!sendToUserIdeaId} onOpenChange={(open) => { if (!open) { setSendToUserIdeaId(null); setSendToUserIdeaTitle(""); } }} />

        {/* Welcome Tour (first visit) */}
        {showWelcomeTour && (
          <WelcomeTour
            onClose={() => {
              setShowWelcomeTour(false);
              localStorage.setItem("audio-ideias-tour-seen", "1");
            }}
            onStartGuide={() => {
              setShowWelcomeTour(false);
              localStorage.setItem("audio-ideias-tour-seen", "1");
              setTimeout(() => setShowInteractiveGuide(true), 300);
            }}
          />
        )}

        {/* Interactive Guide */}
        {showInteractiveGuide && (
          <InteractiveGuide onClose={() => setShowInteractiveGuide(false)} />
        )}
      </div>
    </TooltipProvider>
  );
};

export default VoiceIdeias;
